/*
 * richtext-extensions.ts — SOURCE DE VÉRITÉ UNIQUE des extensions Tiptap de
 * l'éditeur RichText Puck (propriétaire : inventozen-backoffice). Consommé par les
 * surfaces RichText Puck via `PuckRichTextEditor`. NE PAS réimplémenter les
 * extensions/contournements ailleurs.
 *
 * Sous-ensemble aligné sur le sanitizer (cf. richtext-allowlist : p/strong/em/u/s/
 * span[style=color]/a/ul/ol/li/h2/h3 + text-align). Pas de citation/code/hr en édition Puck v1.
 *
 * Contient les contournements Chrome/ProseMirror du « texte tapé qui devient gras
 * tout seul » — historiquement dans CmsRichTextInline, désormais centralisés ici.
 */
import { Extension } from "@tiptap/core";
import { Bold } from "@tiptap/extension-bold";
import { Heading } from "@tiptap/extension-heading";
import { Italic } from "@tiptap/extension-italic";
import { Link } from "@tiptap/extension-link";
import { Paragraph } from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import { Strike } from "@tiptap/extension-strike";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Plugin } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import type { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { CmsFontColor } from "@/components/tiptap/extensions/FontColor";
import { CmsFontSize } from "@/components/tiptap/extensions/FontSize";
import { Underline as UnderlineTagOnly } from "@/components/tiptap/extensions/Underline";

/** Document RichText vide (canonique). */
export const EMPTY_RICHTEXT_DOC = "<p></p>";

/** Espacement inter-paragraphes par défaut (px) — ≈ ancien `p + p { margin-top: 0.75rem }`. */
export const PARAGRAPH_SPACING_DEFAULT_PX = 12;
/** Bornes de l'input (sanitizer API : 0–200px). */
export const PARAGRAPH_SPACING_MIN_PX = 0;
export const PARAGRAPH_SPACING_MAX_PX = 200;

function clampParagraphSpacingPx(raw: unknown): number {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return PARAGRAPH_SPACING_DEFAULT_PX;
  return Math.max(
    PARAGRAPH_SPACING_MIN_PX,
    Math.min(PARAGRAPH_SPACING_MAX_PX, Math.round(n)),
  );
}

/**
 * Paragraphe Puck avec `style="margin-bottom: Npx"` (input px dans la toolbar).
 * Rétro-compat : lit aussi les anciennes classes `spacing-paragraph-{2|4|6}`.
 */
const PuckParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      paragraphSpacing: {
        default: PARAGRAPH_SPACING_DEFAULT_PX,
        parseHTML: (element) => {
          const style = element.getAttribute("style") || "";
          const styleMatch = style.match(
            /margin-bottom:\s*(\d+(?:\.\d+)?)\s*px/i,
          );
          if (styleMatch) return clampParagraphSpacingPx(styleMatch[1]);
          const cls = element.getAttribute("class") || "";
          const classMatch = cls.match(/spacing-paragraph-(\d+)/);
          if (classMatch) return clampParagraphSpacingPx(classMatch[1]);
          return PARAGRAPH_SPACING_DEFAULT_PX;
        },
        renderHTML: (attributes) => {
          const px = clampParagraphSpacingPx(attributes.paragraphSpacing);
          return { style: `margin-bottom: ${px}px` };
        },
      },
    };
  },
});

/**
 * Gras/Italique restreints aux BALISES (`<strong>`, `<em>`). Par défaut TipTap parse
 * aussi `<b>/<i>` et `font-weight ≥500 → gras` : or la typo du thème applique un
 * font-weight 500 hérité, et Chrome enrobe le caractère tapé d'un `<b>` (typing style
 * du caret) que ProseMirror reparserait en gras → texte tapé gras involontaire. En ne
 * parsant QUE `<strong>`/`<em>` (ce que notre bouton produit), l'injection est neutralisée.
 */
const BoldTagOnly = Bold.extend({
  parseHTML() {
    return [{ tag: "strong" }];
  },
});
const ItalicTagOnly = Italic.extend({
  parseHTML() {
    return [{ tag: "em" }];
  },
});

/** Barré — balise `<s>` uniquement (allowlist). */
const StrikeTagOnly = Strike.extend({
  parseHTML() {
    return [{ tag: "s" }];
  },
});

/**
 * CŒUR DU FIX « texte tapé qui devient gras tout seul ». Chrome pose un « typing
 * style » gras sur le caret, matérialisé en `storedMarks` fantôme dans une transaction
 * qui NE modifie PAS le document → le caractère suivant hérite du gras. On efface donc
 * tout `storedMarks` posé SANS édition du document.
 */
const StripPhantomStoredMarks = Extension.create({
  name: "stripPhantomStoredMarks",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction(transactions, _oldState, newState) {
          const docChanged = transactions.some((tr) => tr.docChanged);
          const hasStored =
            newState.storedMarks && newState.storedMarks.length > 0;
          if (!docChanged && hasStored) {
            return newState.tr.setStoredMarks(null);
          }
          return null;
        },
      }),
    ];
  },
});

/**
 * Extensions de l'éditeur RichText Puck. Sous-ensemble du sanitizer + couleurs inline
 * (`span[style=color]` via TextStyle + CmsFontColor). Le `placeholder` est paramétrable.
 */
export function createPuckRichTextExtensions(opts?: { placeholder?: string }) {
  return [
    StarterKit.configure({
      paragraph: false,
      heading: false,
      blockquote: false,
      codeBlock: false,
      code: false,
      horizontalRule: false,
      strike: false,
      bold: false,
      italic: false,
    }),
    PuckParagraph,
    BoldTagOnly,
    ItalicTagOnly,
    UnderlineTagOnly,
    StrikeTagOnly,
    Heading.configure({ levels: [2, 3] }),
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    TextStyle,
    CmsFontColor,
    CmsFontSize,
    StripPhantomStoredMarks,
    // `target` est désormais choisi PAR LIEN (case « ouvrir dans un nouvel onglet »
    // de l'éditeur de lien) au lieu d'être forcé à `_blank` pour tous. Les liens
    // existants gardent leur `target` (parsé du HTML stocké) ; les nouveaux liens
    // sont en même onglet par défaut, sauf case cochée. Le sanitizer API ajoute
    // `rel="noopener noreferrer"` dès qu'un `target="_blank"` est présent.
    //
    // `cms-page` : jeton stable des liens « Page du site » (cf. LinkEditor /
    // resolveCmsPageHrefs). Sans ce protocole TipTap refuse `setLink` /
    // vide le href au serialize → les liens internes ne s'ouvraient pas.
    Link.configure({
      openOnClick: false,
      protocols: ["http", "https", "mailto", "cms-page"],
    }),
    Placeholder.configure({
      placeholder: opts?.placeholder ?? "Écrivez votre texte…",
    }),
  ];
}

/**
 * État actif d'une MARQUE basé sur le DOCUMENT réel, en IGNORANT les `storedMarks`
 * (sinon le bouton clignote actif sur du texte non gras à cause du typing style Chrome).
 */
export function markActiveInDoc(editor: Editor, name: string): boolean {
  const { state } = editor;
  const type = state.schema.marks[name];
  if (!type) return false;
  const { from, to, empty, $from } = state.selection;
  if (empty) return !!type.isInSet($from.marks());
  return state.doc.rangeHasMark(from, to, type);
}

/**
 * GARDE DÉFINITIVE contre le « texte tapé qui devient gras ». À brancher sur
 * `editorProps.handleTextInput`. Si un storedMark fantôme est présent à la frappe, on
 * insère le texte avec les marques RÉELLES du contexte gauche (jamais le fantôme).
 */
export function handleRichTextInput(
  view: EditorView,
  from: number,
  to: number,
  text: string,
): boolean {
  const stored = view.state.storedMarks;
  if (!stored || stored.length === 0) return false;
  const { state } = view;
  const contextMarks = state.doc.resolve(from).marks();
  view.dispatch(state.tr.setStoredMarks(contextMarks).insertText(text, from, to));
  return true;
}
